import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Button, Dialog, DialogContent, DialogTitle, IconButton,
  Typography, Grid, TextField, Autocomplete, Tooltip, Link
} from '@mui/material';
import { Close, Add, Edit, ContentCopy } from '@mui/icons-material';

import { ToastV3 } from '../../../components';
import { MRTTable } from '../../../../../components';
import { SMPAPI } from '../../../../../fetch/smp';
import { Routes } from '../../../../../constants/routes';

import { AddEditEmployeeAppLicenses } from './AddEditEmployeeAppLicenses';




export interface EmployeeAppLicense {
  id: string;
  applicationId: number;
  appLogoUrl: string;
  appName: string;
  licenseId: number | null;
  license: string;
  status: string;
}
export interface EmployeeProfileModalProps {
  open: boolean;
  onClose: () => void;
  employee: {
    id: string;
    employeeId?: number;
    name: string;
    procurementStatus: string;
    type?: string;
    email?: string;
    aadStatus?: string;
    hireDate?: string;
    title?: string;
    worksite?: string;
    office?: string;
    department?: string;
    primaryProductType?: string;
    primarySpecialtyType?: string;
    reportingManager?: string;
    notes?: string;
  };
  onEditLicense: (license: EmployeeAppLicense) => void;
  onAddLicense: () => void;
  onSave: (data: any) => void;
}

const FIELDS: { key: keyof EmployeeProfileModalProps['employee'], label: string, editable?: boolean }[] = [
  { key: 'procurementStatus', label: 'Procurement Status:', editable: true },
  { key: 'type', label: 'Type:', editable: false },
  { key: 'email', label: 'Email:', editable: false },
  { key: 'employeeId', label: 'Employee Number:', editable: false },
  { key: 'aadStatus', label: 'AAD Status:', editable: false },
  { key: 'hireDate', label: 'Hire Date:', editable: false },
  { key: 'title', label: 'Title:', editable: false },
  { key: 'worksite', label: 'Worksite:', editable: false },
  { key: 'office', label: 'Office:', editable: false },
  { key: 'department', label: 'Department:', editable: false },
  { key: 'primaryProductType', label: 'Primary Product Type:', editable: false },
  { key: 'primarySpecialtyType', label: 'Primary Specialty Type:', editable: false },
  { key: 'reportingManager', label: 'Reporting Manager:', editable: false },
  { key: 'notes', label: 'Notes:', editable: true },
];

export const EmployeeProfileModal: React.FC<EmployeeProfileModalProps> = ({
  open, onClose, employee, onEditLicense, onAddLicense, onSave
}) => {
  const [procurementStatus, setProcurementStatus] = useState(employee.procurementStatus || '');
  const [procurementStatusId, setProcurementStatusId] = useState<number | null>(null);
  const [procurementStatusOptions, setProcurementStatusOptions] = useState<{ value: number, label: string }[]>([]);
  const [notes, setNotes] = useState(employee.notes || '');
    // Edit mode for individual fields
  const [editField, setEditField] = useState<string | null>(null);

  // Fetch procurement status options from backend
  useEffect(() => {
    SMPAPI.fetchProcurementStatusOptions().then(setProcurementStatusOptions);
  }, []);

  // Track dirty state for notes/procurementStatus
  const [fieldDirty, setFieldDirty] = useState(false);
  const prevNotes = useRef(notes);
  const prevProcStatus = useRef(procurementStatus);
  const userHasInteracted = useRef(false);

 
  useEffect(() => {
    if (prevNotes.current !== notes || prevProcStatus.current !== procurementStatus) {
      if (userHasInteracted.current) {
        setFieldDirty(true);
      }
    }
    prevNotes.current = notes;
    prevProcStatus.current = procurementStatus;
  }, [notes, procurementStatus]);

  // Mark user interaction on change
  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    userHasInteracted.current = true;
    setNotes(e.target.value);
  };
  const handleProcurementStatusChange = (_: any, newValue: any) => {
    userHasInteracted.current = true;
    if (typeof newValue === 'string') {
      setProcurementStatus(newValue);
      setProcurementStatusId(null);
    } else if (newValue) {
      setProcurementStatus(newValue.label);
      setProcurementStatusId(newValue.value);
    } else {
      setProcurementStatus('');
      setProcurementStatusId(null);
    }
  };

  // Track if Employee SMP Profile exists for this employee
  const [profileExists, setProfileExists] = useState<boolean>(false);
  // Fetch Employee SMP Profile on open/employee change
  useEffect(() => {
    const empId = employee.employeeId ?? employee.id;
    const numericId = Number(empId);
    if (open && numericId && !isNaN(numericId)) {
      SMPAPI.fetchEmployeeSmpProfile(numericId).then((profile) => {
        if (profile) {
          setProcurementStatus(profile.procurementStatus || '');
          setProcurementStatusId(profile.procurementStatusId ?? null);
          setNotes(profile.notes || '');
          setProfileExists(true);
        } else {
          setProcurementStatus(employee.procurementStatus || '');
          setProcurementStatusId(null);
          setNotes(employee.notes || '');
          setProfileExists(false);
        }
      });
    } else {
      setProcurementStatus(employee.procurementStatus || '');
      setProcurementStatusId(null);
      setNotes(employee.notes || '');
      setProfileExists(false);
    }
  }, [open, employee]);
  const [licenses, setLicenses] = useState<EmployeeAppLicense[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLicenses, setTotalLicenses] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeout = useRef<NodeJS.Timeout | null>(null);

  // Modal state for add/edit license
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [licenseModalInitialData, setLicenseModalInitialData] = useState<any>(null);
  const [availableApplications, setAvailableApplications] = useState<any[]>([]);
  // const [availableLicenses, setAvailableLicenses] = useState<any[]>([]);
  // const [loadingLicenses, setLoadingLicenses] = useState(false);

  // MRT states
  const [mrtSorting, setMrtSorting] = useState<any[]>([]);
  const [mrtColumnFilters, setMrtColumnFilters] = useState<any[]>([]);
  const [mrtGlobalFilter, setMrtGlobalFilter] = useState('');
  const mrtSearchDebounceRef = useRef<any>(null);

  // Cleanup search debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (mrtSearchDebounceRef.current) {
        clearTimeout(mrtSearchDebounceRef.current);
      }
    };
  }, []);



// Build server query params from current UI/MRT state
const buildQueryParams = useCallback((opts?: { page?: number; perPage?: number }) => {
  // Build server-side sort param from MRT sorting state for global sorting across pages
  const allowedSortIds = new Set([
    'appName',
    'license', 
    'status',
  ]);
  const sortString = (Array.isArray(mrtSorting) ? mrtSorting : [])
    .map((s: any) => {
      const id = s.id || s.columnId || s.accessorKey;
      if (!id || !allowedSortIds.has(id)) return null;
      const dir = s.desc ? 'desc' : 'asc';
      return `${id}:${dir}`;
    })
    .filter(Boolean)
    .join(',');

  const pageIdx = opts?.page ?? (page + 1);
  const pageSize = opts?.perPage ?? rowsPerPage;
  
  // Build search from column filters and global filter
  const columnSearchTerms = (Array.isArray(mrtColumnFilters) ? mrtColumnFilters : [])
    .map((filter: any) => filter.value)
    .filter(Boolean);
  const globalSearchTerm = mrtGlobalFilter ? [mrtGlobalFilter] : [];
  const allSearchTerms = [...columnSearchTerms, ...globalSearchTerm];
  const searchString = allSearchTerms.length > 0 ? allSearchTerms.join(' ') : undefined;
  
  return {
    page: pageIdx,
    perPage: pageSize,
    showInactive: showInactive,
    search: searchString,
    sort: sortString || undefined,
  };
}, [mrtSorting, mrtColumnFilters, mrtGlobalFilter, page, rowsPerPage, showInactive]);

const loadAssignments = useCallback(async () => {
  const empId = employee.employeeId ?? employee.id;
  const numericId = Number(empId);
  if (!open || !numericId || isNaN(numericId)) {
    setLicenses([]);
    setTotalLicenses(0);
    return;
  }
  
  setLoading(true);
  try {
    const params = buildQueryParams();
    const res = await SMPAPI.fetchEmployeeAssignments(numericId, params);
    const data = res.records || [];
    setTotalLicenses(res.totalRecordCount || 0);
    
    if (!Array.isArray(data)) {
      setLicenses([]);
      return;
    }
    
    const mapped = await Promise.all(
      data.map(async (item) => {
        let appLogoUrl = '';
        if (item.applicationLogo) {
          try {
            appLogoUrl = await SMPAPI.getApplicationLogoUrl(Number(item.applicationLogo));
          } catch (e) {
            appLogoUrl = '';
          }
        }
        return {
          id: String(item.applicationUserID ?? item.applicationID ?? Math.random()),
          applicationId: item.applicationID ?? 0,
          appLogoUrl,
          appName: item.applicationName ?? '',
          licenseId: item.licenseID ?? null,
          license: item.assignedLicense ?? item.licenseName ?? '',
          status: item.userApplicationStatus ?? item.licenseStatus ?? '',
        };
      })
    );
    setLicenses(mapped);
  } catch (error) {
    console.error('Failed to load assignments:', error);
    setLicenses([]);
    setTotalLicenses(0);
  } finally {
    setLoading(false);
  }
}, [open, employee, buildQueryParams]);

// Load assignments when dependencies change
useEffect(() => {
  loadAssignments();
}, [loadAssignments]);

// Reload when MRT state changes (sorting, filtering)
useEffect(() => {
  loadAssignments();
}, [loadAssignments, mrtSorting, mrtColumnFilters, mrtGlobalFilter]);

  const handleCopy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    if (copyTimeout.current) clearTimeout(copyTimeout.current);
    copyTimeout.current = setTimeout(() => setCopied(false), 1200);
  };

  
  const handleEditLicense = useCallback(async (license: any) => {
    
    let userRecord = null;
    if (license.id) {
      try {
        userRecord = await SMPAPI.getUser(license.id);
      } catch (e) {
        userRecord = null;
      }
    }
    const empId = employee.employeeId ?? Number(employee.id);
    setLicenseModalInitialData({
      ...(userRecord || license),
      applicationID: availableApplications.find(a => a.label === license.appName)?.value || '',
      employeeID: empId,
      userName: employee.name,
      email: employee.email || '',
    });
    setLicenseModalOpen(true);
  }, [employee, availableApplications]);

  // MRT columns for licenses
  const mrtColumns = useMemo(() => {
    const ellipsisCell = (text: any, maxWidth: number = 250) => (
      <Tooltip title={text ?? ''} arrow>
        <Box component="span" sx={{ display: 'inline-block', maxWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text ?? ''}</Box>
      </Tooltip>
    );

    return [
      {
        accessorKey: 'appLogoUrl',
        header: 'Logo',
        enableColumnFilter: false,
        enableSorting: false,
        size: 100,
        minSize: 100,
        maxSize: 100,
        muiTableBodyCellProps: { sx: { width: 100, minWidth: 100, maxWidth: 100, overflow: 'visible' } },
        Cell: ({ cell }: any) => {
          const value = cell.getValue();
          return value ? <img src={value} alt="logo" style={{ height: 30, width: 'auto', maxWidth: 80, borderRadius: 3, objectFit: 'contain' }} /> : null;
        },
      },
      {
        accessorKey: 'appName',
        header: 'Application Name',
        filterVariant: 'text',
        size: 200,
        muiTableBodyCellProps: { sx: { maxWidth: 200 } },
        Cell: ({ row, cell }: any) => {
          const appName = cell.getValue();
          const { applicationId } = row.original;
          if (!appName) return null;
          if (applicationId) {
            return (
              <Tooltip title={appName} arrow>
                <Link
                  href={Routes.viewApplication(applicationId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-block',
                    maxWidth: 200,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textDecoration: 'none',
                    color: '#1976d2',
                    cursor: 'pointer',
                  }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  {appName}
                </Link>
              </Tooltip>
            );
          }
          return ellipsisCell(appName, 200);
        },
      },
      {
        accessorKey: 'license',
        header: 'Assigned License',
        filterVariant: 'text',
        size: 180,
        muiTableBodyCellProps: { sx: { maxWidth: 180 } },
        Cell: ({ row, cell }: any) => {
          const licenseName = cell.getValue();
          const { applicationId, licenseId } = row.original;
          if (!licenseName) return null;
          if (applicationId && licenseId > 0) {
            return (
              <Tooltip title={licenseName} arrow>
                <Link
                  href={`${Routes.applicationLicenses(applicationId)}?licenseId=${licenseId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-block',
                    maxWidth: 180,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textDecoration: 'none',
                    color: '#1976d2',
                    cursor: 'pointer',
                  }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  {licenseName}
                </Link>
              </Tooltip>
            );
          }
          return ellipsisCell(licenseName, 180);
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'text',
        size: 120,
        muiTableBodyCellProps: { sx: { maxWidth: 120 } },
        Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 120),
      },
      {
        id: 'actions',
        header: 'Edit',
        enableColumnFilter: false,
        enableSorting: false,
        size: 60,
        Cell: ({ row }: any) => (
          <IconButton size="small" onClick={() => handleEditLicense(row.original)}>
            <Edit fontSize="small" sx={{ fontSize: 20 }} />
          </IconButton>
        ),
      },
    ];
  }, [handleEditLicense]);

  // Fetch all available applications for this employee (for add/edit license modal)
  useEffect(() => {
    if (!open) return;
    SMPAPI.fetchApplications({ perPage: 1000, page: 1 }).then(res => {
      setAvailableApplications(
        (res.records || []).map((a: any) => ({
          value: a.applicationID?.toString() || '',
          label: a.name || `App ${a.applicationID}`,
          logoUrl: a.logoUrl || '',
        }))
      );
    });
  }, [open]);

  // Handler to open add license modal
  const handleAddLicense = () => {
    const empId = employee.employeeId ?? Number(employee.id);
    setLicenseModalInitialData({
      applicationID: availableApplications[0]?.value || '',
      employeeID: empId,
      userName: employee.name,
      email: employee.email || '',
    });
    setLicenseModalOpen(true);
  };

  const handleSave = async () => {
    const empId = employee.employeeId ?? employee.id;
    const numericId = Number(empId);
    if (!numericId || isNaN(numericId)) return;
    const payload = {
      employeeId: numericId,
      notes,
      procurementStatus,
      procurementStatusId: procurementStatusId ?? undefined,
    };
    if (profileExists) {
      // Update if profile exists
      await SMPAPI.updateEmployeeSmpProfile(numericId, payload);
    } else {
      // Create if profile does not exist
      await SMPAPI.createEmployeeSmpProfile(payload);
    }
    onSave({ ...employee, procurementStatus, notes });
    // Do NOT setFieldDirty(false) here, only in ToastV3 onSave
  };

  // Reset editable fields on employee change
  useEffect(() => {
    setProcurementStatus(employee.procurementStatus || '');
    setNotes(employee.notes || '');
    setFieldDirty(false);
    prevNotes.current = employee.notes || '';
    prevProcStatus.current = employee.procurementStatus || '';
  }, [employee]);

  // Styles inspired by ApplicationDetails.tsx
  const denseLabelStyle = {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#333',
    marginBottom: 0,
    lineHeight: 1.2,
  };
  const denseValueStyle = {
    fontSize: '0.8rem',
    color: '#333',
    lineHeight: 1.2,
  };
  const denseInputStyle = {
    width: '100%',
    maxWidth: 300,
    borderRadius: '2px',
    '& .MuiInputBase-root': {
      height: 36,
      fontSize: '0.875rem',
    },
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          overflowX: 'hidden',
          width: '90vw',
          maxWidth: 1600,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Employee Profile</Typography>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          {/* Left vertical labels/fields - wider pane */}
          <Grid
            item
            xs={12}
            md={5}
            lg={5}
            sx={{
              flexBasis: { md: 'calc(41.6667% - 20px)', lg: 'calc(41.6667% - 20px)' },
              maxWidth: { md: 'calc(41.6667% - 20px)', lg: 'calc(41.6667% - 20px)' },
            }}
          >
            <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 2, minHeight: 510 }}>
              <Grid container direction="column" spacing={0} sx={{ rowGap: 0.2 }}>
                {/* Name row above Procurement Status */}
                <Grid item sx={{ mb: 1 }}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 0 }}>{employee.name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 600, textTransform: 'none', border: '2px double #00008B' }}
                        onClick={() => {
                          window.location.href = '/smp/employees';
                          if (onClose) onClose();
                        }}
                      >
                        All Employees
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                {/* ...existing code... */}
                {FIELDS.map((field) => (
                  field.key === 'procurementStatus' ? (
                    <Grid item key={field.key} sx={{ mb: 0.2 }}>
                      <Grid container alignItems="center" spacing={1} wrap="nowrap">
                        <Grid item xs={4} sx={{ flexBasis: 'calc(33.333% + 10px)', maxWidth: 'calc(33.333% + 10px)' }}>
                          <Typography sx={denseLabelStyle}>{field.label}</Typography>
                        </Grid>
                        <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', flexBasis: 'calc(66.667% - 10px)', maxWidth: 'calc(66.667% - 10px)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', ml: -0.5 }}>
                            <Autocomplete
                              fullWidth
                              size="small"
                              options={procurementStatusOptions}
                              getOptionLabel={option => typeof option === 'string' ? option : option.label}
                              value={
                                procurementStatusOptions.find(opt => opt.label === procurementStatus) ||
                                (procurementStatus ? { label: procurementStatus, value: procurementStatusId ?? 0 } : null)
                              }
                              onChange={handleProcurementStatusChange}
                              renderInput={(params) => (
                                <TextField {...params} sx={denseInputStyle} placeholder="Select procurement status" />
                              )}
                              disabled={editField !== 'procurementStatus'}
                            />
                            {editField !== 'procurementStatus' && (
                              <IconButton size="small" sx={{ ml: 1 }} onClick={() => setEditField('procurementStatus')}>
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : field.key === 'notes' ? (
                    <Grid item key={field.key} sx={{ mb: 0.1, mt: 0.5 }}>
                      <Box>
                        <Typography sx={{ ...denseLabelStyle, mb: 0.5 }}>Notes</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            rows={5}
                            value={notes}
                            onChange={handleNotesChange}
                            placeholder=""
                            sx={{
                              ...denseInputStyle,
                              mt: 0,
                              '& .MuiInputBase-root': {
                                ...denseInputStyle['& .MuiInputBase-root'],
                                alignItems: 'flex-start',
                                height: 'auto',
                                minHeight: 120,
                              },
                              textarea: {
                                overflow: 'auto',
                                resize: 'vertical',
                                boxSizing: 'border-box',
                              },
                            }}
                            disabled={editField !== 'notes'}
                            onBlur={() => setEditField(null)}
                          />
                          {editField !== 'notes' && (
                            <IconButton size="small" sx={{ ml: 1, alignSelf: 'flex-start', mt: 1 }} onClick={() => setEditField('notes')}>
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  ) : field.key === 'email' ? (
                    <Grid item key={field.key} sx={{ mb: 0.1 }}>
                      <Grid container alignItems="center" wrap="nowrap">
                        <Grid item xs={4} sx={{ flexBasis: 'calc(33.333% + 10px)', maxWidth: 'calc(33.333% + 10px)' }}>
                          <Typography sx={denseLabelStyle}>{field.label}</Typography>
                        </Grid>
                        <Grid item xs={8} sx={{ flexBasis: 'calc(66.667% - 10px)', maxWidth: 'calc(66.667% - 10px)' }}>
                          {employee.email ? (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              flexWrap: 'nowrap',
                              minWidth: 0,
                              gap: 0.5,
                            }}>
                              <Tooltip title={employee.email} placement="top">
                                <Typography
                                  sx={{
                                    ...denseValueStyle,
                                    cursor: 'pointer',
                                    color: '#1976d2',
                                    textDecoration: 'underline',
                                    mr: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    textOverflow: 'clip',
                                    flex: '0 1 auto',
                                    minWidth: 0,
                                    display: 'inline-block',
                                  }}
                                  component="a"
                                  href={`mailto:${employee.email}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {employee.email}
                                </Typography>
                              </Tooltip>
                              <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
                                <IconButton
                                  size="small"
                                  sx={{ ml: 0.5, p: 0.5, flex: 'none', alignSelf: 'center', height: 24, width: 24 }}
                                  onClick={() => handleCopy(employee.email!)}
                                >
                                  <ContentCopy sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography sx={denseValueStyle}>-</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid item key={field.key} sx={{ mb: 0.1 }}>
                      <Grid container alignItems="center" wrap="nowrap">
                        <Grid item xs={4} sx={{ flexBasis: 'calc(33.333% + 10px)', maxWidth: 'calc(33.333% + 10px)' }}>
                          <Typography sx={denseLabelStyle}>{field.label}</Typography>
                        </Grid>
                        <Grid item xs={8} sx={{ flexBasis: 'calc(66.667% - 10px)', maxWidth: 'calc(66.667% - 10px)' }}>
                          <Typography sx={denseValueStyle}>{(employee as any)[field.key] || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  )
                ))}
              </Grid>
            </Box>
          </Grid>
          {/* Right: Applications & Licenses Table - slightly less width */}
          <Grid
            item
            xs={12}
            md={7}
            lg={7}
            sx={{
              flexBasis: { md: 'calc(58.3333% + 20px)', lg: 'calc(58.3333% + 20px)' },
              maxWidth: { md: 'calc(58.3333% + 20px)', lg: 'calc(58.3333% + 20px)' },
            }}
          >
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="subtitle1" fontWeight="bold">Applications & Licenses</Typography>
              <Box>
                <Button
                  variant={showInactive ? 'contained' : 'outlined'}
                  size="small"
                  sx={{ mr: 1 }}
                  onClick={() => setShowInactive(prev => !prev)}
                  aria-pressed={showInactive}
                >
                  {showInactive ? 'Show active' : 'Show inactive'}
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={handleAddLicense} size="small">Add</Button>
              </Box>
            </Box>
            <Box
              sx={{
                minWidth: '100%',
                overflow: 'auto',
                border: '1px solid #e0e0e0', 
                borderRadius: '8px',           
                background: 'white',
                minHeight: Math.max(120, Math.min(420, 48 + 44 * Math.min(licenses.length, rowsPerPage))),
                '& table': {
                  width: '100%',
                  tableLayout: 'auto',
                },
                '& .MuiTableCell-root': {
                  fontSize: '0.75rem',
                  padding: '6px 8px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '250px', 
                  borderBottom: '1px solid rgb(212, 210, 210)', 
                },
                '& .MuiTableCell-head': {
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  overflowWrap: 'normal',
                  wordWrap: 'normal',
                  wordBreak: 'keep-all',
                  hyphens: 'manual',
                  maxWidth: '150px',
                  transition: 'color 0.3s ease',
                  borderBottom: '2px solid rgb(212, 210, 210)',
                },
                '& .MuiTableCell-head:hover, & .MuiTableCell-head:hover *': {
                  color: '#64b5f6',
                  cursor: 'pointer',
                },
                '& th:first-of-type, & td:first-of-type': {
                  minWidth: '80px !important',
                  width: '80px !important',
                  maxWidth: '80px !important',
                  paddingLeft: '8px !important',
                  textAlign: 'center !important',
                },
                '& th:last-of-type, & td:last-of-type': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  textAlign: 'center ! important',
                },
                'td:last-of-type': {
                  p: 0,
                  backgroundColor: '#fff',
                },
                '& .MuiTableCell-body': { py: 0.1 },
                '&::-webkit-scrollbar': { height: 2 },
                '&::-webkit-scrollbar-track': { background: '#e0e0e0' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#a0a0a0',
                  borderRadius: 3,
                },
                scrollbarHeight: 'thin',
                scrollbarColor: '#fff #fff',
              }}
            >
              <MRTTable
                columns={mrtColumns as any}
                data={licenses as any}
                loading={loading}
                manualPagination
                manualSorting
                totalRowCount={totalLicenses}
                pageIndex={page}
                pageSize={rowsPerPage}
                onPaginationChange={(updater: any) => {
                  const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: rowsPerPage }) : updater;
                  if (typeof next?.pageIndex === 'number') setPage(next.pageIndex);
                  if (typeof next?.pageSize === 'number') setRowsPerPage(next.pageSize);
                }}
                onSortingChange={(updater: any) => {
                  const next = typeof updater === 'function' ? updater(mrtSorting) : updater;
                  setMrtSorting(next || []);
                  // Reset to first page so server can repaginate after sort changes
                  setPage(0);
                }}
                globalFilter={mrtGlobalFilter}
                onGlobalFilterChange={(val: any) => {
                  const v = typeof val === 'string' ? val : '';
                  setMrtGlobalFilter(v);
                  // Debounce the search to prevent too many API calls
                  if (mrtSearchDebounceRef.current) {
                    clearTimeout(mrtSearchDebounceRef.current);
                  }
                  mrtSearchDebounceRef.current = setTimeout(() => {
                    if (v.trim().length >= 2 || v.trim().length === 0) {
                      // Reset to first page when searching to search entire dataset
                      setPage(0);
                    }
                  }, 300);
                }}
                sorting={mrtSorting}
                columnFilters={mrtColumnFilters}
                onColumnFiltersChange={(updater: any) => {
                  const next = typeof updater === 'function' ? updater(mrtColumnFilters) : updater;
                  setMrtColumnFilters(next || []);
                  // Reset to first page when filters change to search entire dataset
                  setPage(0);
                }}
              />
              {/* Add/Edit License Modal */}
              <AddEditEmployeeAppLicenses
                open={licenseModalOpen}
                onClose={() => setLicenseModalOpen(false)}
                onSave={async (data) => {
                  setLicenseModalOpen(false);
                  // Refresh assignments table after save using the centralized loader
                  await loadAssignments();
                }}
                initialData={licenseModalInitialData}
                availableApplications={availableApplications}
                applicationID={licenseModalInitialData?.applicationID || (availableApplications[0]?.value || '')}
              />
              {licenses.length === 0 && (
                <Typography variant="body2" align="center" sx={{ mt: 2, fontSize: '0.78rem' }}>
                  No applications/licenses assigned.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
        {/* Toast for unsaved changes */}
        {fieldDirty && userHasInteracted.current && (
          <Box sx={{ position: 'fixed', bottom: 0, left: 0, width: '100%', zIndex: 9999 }}>
            <ToastV3
              type="warning"
              message="You have unsaved changes."
              confirmText="Save"
              onSave={async () => {
                await handleSave();

                setFieldDirty(false);
                userHasInteracted.current = false;
                setEditField(null); 
              }}
              onClose={() => {
                setFieldDirty(false);
                userHasInteracted.current = false;
                setEditField(null); 
              }}
              onCancel={() => {
                setProcurementStatus(employee.procurementStatus || '');
                setNotes(employee.notes || '');
                setFieldDirty(false);
                userHasInteracted.current = false;
                setEditField(null); 
              }}
              showReset={false}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};


